import { c as _c } from "react/compiler-runtime";
import { Card, CardContent, Divider, Stack, styled, Switch, Tooltip, Typography, type Theme } from '@mui/material';
import type { Condition, BackupPolicyConfig } from '@repo/api-gw-sdk';
import { BackupPolicyType, BackupStatus, ConditionOperatorEnum, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { createBackupStatusNotInCondition } from '@/components/backupStatus/createBackupStatusNotInCondition';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { ConfirmationDialog } from '@/components/dialogs/confirmationDialog';
import { Loader } from '@/components/layout/loading';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { backupPolicies } from '@/components/queryBuilder/properties';
import { getResourcesScopeType, type ResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { ResourcesSelectionViewer } from '@/components/resourcesSelection/resourcesSelectionViewer';
import { NavigationLink } from '@/components/shared/link';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import type { BackupVault } from '@/data/vaults/backupVault';
import { createCombineCondition } from '@/types/advanceFilter';
import { BackupPolicyConditionTitle } from './BackupPolicyConditionEditor';
import { isContinuousPolicyType, isNativePolicyType, isPitrPolicyType } from './backupPoliciesTypes';
import { useAllBackupPolicyProperties } from './backupPolicyProperties';
import { BackupPolicyContinuousSchedulesTable, BackupPolicySchedulesTable } from './backupPolicySchedulesTable';
import { BackupPolicyTypeTag } from './backupPolicyTypeTag';
import { BackupPolicyTypeDescription } from './customPolicySupportedDisclaimer';
export const BPCard = styled(Card)((props: {
  disabled: boolean;
  theme?: Theme;
  style?: object;
}) => {
  return {
    border: '1px solid var(--mui-palette-divider)',
    backgroundColor: props.disabled ? 'var(--mui-palette-background-default)' : 'var(--mui-palette-background-paper)',
    ...props.theme?.typography.body2,
    textAlign: 'left',
    color: props.theme?.palette.text.secondary,
    minWidth: '550px',
    minHeight: '170px',
    boxShadow: 'none',
    paddingTop: '12px',
    borderRadius: '16px',
    '& .MuiCardContent-root': {
      padding: '0px 24px 24px'
    },
    ...props.style
  };
});
interface BackupPolicyCardProps {
  processing?: boolean;
  policy: BackupPolicyConfig;
  vaults?: BackupVault[];
  defaultExpanded?: boolean;
  onEdit?: (policy: BackupPolicyConfig) => void;
  onDelete?: (policy: BackupPolicyConfig) => void;
  onDuplicate?: (policy: BackupPolicyConfig) => void;
  onEnabledPolicyUpdate?: (policy: BackupPolicyConfig) => void;
}

// backupPolicyNativeProvider resolves the cloud provider used by the
// AccountAsVaultTag shown on native policy cards. For AZURE_FILES we pin
// to Azure directly (no resourceTypes carried on the policy). For the
// other native types we infer from the first resourceType, falling back
// to AWS for backward compatibility with older policies.
const backupPolicyNativeProvider = (policy: BackupPolicyConfig): Provider => {
  if (policy.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES) {
    return Provider.Azure;
  }
  const first = policy.resourceTypes?.[0];
  return first ? ResourceTypes[first].provider : Provider.Aws;
};
export function BackupPolicyCard(props) {
  const $ = _c(166);
  const enabled = props.policy.enabled;
  const {
    snackbar
  } = useWorkspace();
  const {
    isAuthorized
  } = useRoles();
  const allBackupPolicyProperties = useAllBackupPolicyProperties();
  const [vaultsSectionExpanded, setVaultsSectionExpanded] = useState(!!props.defaultExpanded);
  const [disablePolicyDialogOpen, setDisablePolicyDialogOpen] = useState(false);
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let t0;
  let t1;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let togglePolicyEnabledState;
  if ($[0] !== allBackupPolicyProperties || $[1] !== enabled || $[2] !== isAuthorized || $[3] !== props || $[4] !== snackbar || $[5] !== vaultsSectionExpanded) {
    const matchingVaults = props.vaults?.filter(vault => props.policy?.schedules.find(schedule => schedule.vaultId === vault.id)).slice(0, 2);
    let t13;
    if ($[25] !== props.policy.condition) {
      t13 = getResourcesScopeType(props.policy.condition, "allIncluded");
      $[25] = props.policy.condition;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    const resourcesScopeType = t13;
    let t14;
    if ($[27] !== props || $[28] !== snackbar) {
      t14 = () => {
        const enabled_0 = props.policy.enabled;
        props.onEnabledPolicyUpdate?.(props.policy);
        snackbar.showMessage(<><span className="font-semibold">{props.policy.name}</span> backup policy is now {enabled_0 ? "disabled" : "enabled"}.</>);
      };
      $[27] = props;
      $[28] = snackbar;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    togglePolicyEnabledState = t14;
    let t15;
    if ($[30] !== enabled || $[31] !== togglePolicyEnabledState) {
      t15 = () => {
        if (enabled) {
          setDisablePolicyDialogOpen(true);
        } else {
          togglePolicyEnabledState();
        }
      };
      $[30] = enabled;
      $[31] = togglePolicyEnabledState;
      $[32] = t15;
    } else {
      t15 = $[32];
    }
    const onToggleClicked = t15;
    const t16 = props.policy?.id || "";
    let T5;
    let T6;
    let t17;
    let t18;
    let t19;
    let t20;
    let t21;
    let t22;
    if ($[33] !== enabled || $[34] !== isAuthorized || $[35] !== props.onDelete || $[36] !== props.onDuplicate || $[37] !== props.onEdit || $[38] !== props.onEnabledPolicyUpdate || $[39] !== props.policy.attachedResources || $[40] !== props.policy.name || $[41] !== props.policy.policyType || $[42] !== props.processing || $[43] !== t16) {
      const inventoryFilterCondition = {
        operator: ConditionOperatorEnum.ContainsAnyOf,
        value: [t16],
        property: backupPolicies.name
      };
      T4 = BPCard;
      t11 = !enabled;
      t12 = "backup-policy-card";
      T6 = Stack;
      t20 = "row";
      if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
        t21 = {
          maxWidth: "100%",
          justifyContent: "space-between",
          gap: "12px",
          alignItems: "center",
          margin: "12px 24px"
        };
        $[55] = t21;
      } else {
        t21 = $[55];
      }
      let t23;
      if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
        t23 = {
          alignItems: "center",
          gap: "12px",
          minWidth: "0px",
          flexShrink: 1
        };
        $[56] = t23;
      } else {
        t23 = $[56];
      }
      let t24;
      if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
        t24 = {
          minWidth: "0px",
          flexShrink: 1
        };
        $[57] = t24;
      } else {
        t24 = $[57];
      }
      let t25;
      if ($[58] !== props.policy.name) {
        t25 = <Stack sx={t24}><OverflowTooltip text={props.policy.name} renderContent={_temp} /></Stack>;
        $[58] = props.policy.name;
        $[59] = t25;
      } else {
        t25 = $[59];
      }
      let t26;
      if ($[60] !== props.policy.policyType) {
        t26 = <BackupPolicyTypeTag policyType={props.policy.policyType} />;
        $[60] = props.policy.policyType;
        $[61] = t26;
      } else {
        t26 = $[61];
      }
      if ($[62] !== t25 || $[63] !== t26) {
        t22 = <Stack direction="row" sx={t23}>{t25}{t26}</Stack>;
        $[62] = t25;
        $[63] = t26;
        $[64] = t22;
      } else {
        t22 = $[64];
      }
      T5 = Stack;
      t17 = "row";
      if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = {
          alignItems: "center",
          gap: "16px"
        };
        $[65] = t18;
      } else {
        t18 = $[65];
      }
      t19 = enabled && <>{props.processing ? <Loader variant="inline" /> : <NavigationLink path="/inventory" searchParams={{
          query: createCombineCondition([inventoryFilterCondition, createBackupStatusNotInCondition([BackupStatus.ExcludedFromBackup, BackupStatus.Disconnected, BackupStatus.Unsupported, BackupStatus.LimitExceeded])])
        }} disabled={!isAuthorized("read:inventory")} className="text-(--mui-palette-primary-main)"><ButtonWithIcon text={`${props.policy.attachedResources ?? 0} Resources`} iconPosition="right" icon="material-symbols-arrow-right-alt" /></NavigationLink>}{(props.onEdit && props.onDelete && props.onDuplicate || props.onEnabledPolicyUpdate) && <Divider sx={{
          height: "16px"
        }} orientation="vertical" />}</>;
      $[33] = enabled;
      $[34] = isAuthorized;
      $[35] = props.onDelete;
      $[36] = props.onDuplicate;
      $[37] = props.onEdit;
      $[38] = props.onEnabledPolicyUpdate;
      $[39] = props.policy.attachedResources;
      $[40] = props.policy.name;
      $[41] = props.policy.policyType;
      $[42] = props.processing;
      $[43] = t16;
      $[44] = T4;
      $[45] = T5;
      $[46] = T6;
      $[47] = t11;
      $[48] = t12;
      $[49] = t17;
      $[50] = t18;
      $[51] = t19;
      $[52] = t20;
      $[53] = t21;
      $[54] = t22;
    } else {
      T4 = $[44];
      T5 = $[45];
      T6 = $[46];
      t11 = $[47];
      t12 = $[48];
      t17 = $[49];
      t18 = $[50];
      t19 = $[51];
      t20 = $[52];
      t21 = $[53];
      t22 = $[54];
    }
    let t23;
    if ($[66] !== isAuthorized || $[67] !== props) {
      t23 = props.onEdit && props.onDelete && props.onDuplicate && <OptionMenu options={[{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "duplicate-policy",
        text: "Duplicate",
        menuItemProps: {
          disabled: !isAuthorized("create:policies"),
          onClick: () => {
            props.onDuplicate?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }, {
        id: "delete-policy",
        text: "Delete",
        menuItemProps: {
          disabled: !isAuthorized("delete:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onDelete?.(props.policy);
          }
        }
      }]} />;
      $[66] = isAuthorized;
      $[67] = props;
      $[68] = t23;
    } else {
      t23 = $[68];
    }
    let t24;
    if ($[69] !== enabled || $[70] !== isAuthorized || $[71] !== onToggleClicked || $[72] !== props.onEnabledPolicyUpdate) {
      t24 = props.onEnabledPolicyUpdate && <Tooltip title={enabled ? "This policy is enabled" : "This policy is disabled"}><Switch disabled={!isAuthorized("update:policies")} checked={enabled} onChange={onToggleClicked} /></Tooltip>;
      $[69] = enabled;
      $[70] = isAuthorized;
      $[71] = onToggleClicked;
      $[72] = props.onEnabledPolicyUpdate;
      $[73] = t24;
    } else {
      t24 = $[73];
    }
    let t25;
    if ($[74] !== T5 || $[75] !== t17 || $[76] !== t18 || $[77] !== t19 || $[78] !== t23 || $[79] !== t24) {
      t25 = <T5 direction={t17} sx={t18}>{t19}{t23}{t24}</T5>;
      $[74] = T5;
      $[75] = t17;
      $[76] = t18;
      $[77] = t19;
      $[78] = t23;
      $[79] = t24;
      $[80] = t25;
    } else {
      t25 = $[80];
    }
    if ($[81] !== T6 || $[82] !== t20 || $[83] !== t21 || $[84] !== t22 || $[85] !== t25) {
      t10 = <T6 direction={t20} sx={t21}>{t22}{t25}</T6>;
      $[81] = T6;
      $[82] = t20;
      $[83] = t21;
      $[84] = t22;
      $[85] = t25;
      $[86] = t10;
    } else {
      t10 = $[86];
    }
    T3 = CardContent;
    if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        marginTop: "24px"
      };
      $[87] = t7;
    } else {
      t7 = $[87];
    }
    const t26 = props.defaultExpanded;
    let t27;
    if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
      t27 = <AccordionSummary><Typography variant="h4">{"Resource selection"}</Typography></AccordionSummary>;
      $[88] = t27;
    } else {
      t27 = $[88];
    }
    let t28;
    if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = {
        gap: "24px"
      };
      $[89] = t28;
    } else {
      t28 = $[89];
    }
    const t29 = props.policy.condition;
    const t30 = props.policy.policyType ?? BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD;
    let t31;
    if ($[90] !== props.policy.resourceTypes || $[91] !== t30) {
      t31 = <BackupPolicyConditionTitle resourceTypes={props.policy.resourceTypes} policyType={t30} />;
      $[90] = props.policy.resourceTypes;
      $[91] = t30;
      $[92] = t31;
    } else {
      t31 = $[92];
    }
    let t32;
    if ($[93] !== props.policy.include) {
      t32 = props.policy.include ?? [];
      $[93] = props.policy.include;
      $[94] = t32;
    } else {
      t32 = $[94];
    }
    let t33;
    if ($[95] !== props.policy.exclude) {
      t33 = props.policy.exclude ?? [];
      $[95] = props.policy.exclude;
      $[96] = t33;
    } else {
      t33 = $[96];
    }
    const t34 = isPitrPolicyType(props.policy.policyType) ? "This backup policy will be automatically applied to all resources of the selected type. When new ones are discovered, they\u2019ll be included too." : "This backup policy will be automatically applied to all resources. When new ones are discovered, they\u2019ll be included too.";
    let t35;
    if ($[97] !== allBackupPolicyProperties) {
      t35 = allBackupPolicyProperties.flat();
      $[97] = allBackupPolicyProperties;
      $[98] = t35;
    } else {
      t35 = $[98];
    }
    let t36;
    if ($[99] !== props.policy.condition || $[100] !== props.policy.policyType || $[101] !== props.policy.resourceTypes || $[102] !== resourcesScopeType || $[103] !== t31 || $[104] !== t32 || $[105] !== t33 || $[106] !== t34 || $[107] !== t35) {
      t36 = <ResourcesSelectionViewer condition={t29} conditionTitle={t31} include={t32} exclude={t33} resourcesScopeType={resourcesScopeType} dynamicResourcesHeader={null} allExcludedDescription={"This backup policy won\u2019t be automatically applied to any resources. Only manually included resources will be assigned to the policy."} allIncludedDescription={t34} supportProperties={t35} selectedResourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType} />;
      $[99] = props.policy.condition;
      $[100] = props.policy.policyType;
      $[101] = props.policy.resourceTypes;
      $[102] = resourcesScopeType;
      $[103] = t31;
      $[104] = t32;
      $[105] = t33;
      $[106] = t34;
      $[107] = t35;
      $[108] = t36;
    } else {
      t36 = $[108];
    }
    let t37;
    if ($[109] !== props.policy.policyType) {
      t37 = <BackupPolicyTypeDescription policyType={props.policy.policyType} showForPitr={false} />;
      $[109] = props.policy.policyType;
      $[110] = t37;
    } else {
      t37 = $[110];
    }
    let t38;
    if ($[111] !== t36 || $[112] !== t37) {
      t38 = <AccordionDetails><Stack sx={t28}>{t36}{t37}</Stack></AccordionDetails>;
      $[111] = t36;
      $[112] = t37;
      $[113] = t38;
    } else {
      t38 = $[113];
    }
    if ($[114] !== props.defaultExpanded || $[115] !== t38) {
      t8 = <Accordion defaultExpanded={t26}>{t27}{t38}</Accordion>;
      $[114] = props.defaultExpanded;
      $[115] = t38;
      $[116] = t8;
    } else {
      t8 = $[116];
    }
    if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <Divider sx={{
        margin: "12px -24px"
      }} />;
      $[117] = t9;
    } else {
      t9 = $[117];
    }
    T2 = Accordion;
    t5 = vaultsSectionExpanded;
    if ($[118] !== vaultsSectionExpanded) {
      t6 = () => setVaultsSectionExpanded(!vaultsSectionExpanded);
      $[118] = vaultsSectionExpanded;
      $[119] = t6;
    } else {
      t6 = $[119];
    }
    T1 = AccordionSummary;
    T0 = Stack;
    t0 = "row";
    t1 = "w-full";
    if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        justifyContent: "space-between",
        alignItems: "center"
      };
      t3 = <Typography variant="h4">Backup schedules</Typography>;
      $[120] = t2;
      $[121] = t3;
    } else {
      t2 = $[120];
      t3 = $[121];
    }
    t4 = !vaultsSectionExpanded && <div>{isNativePolicyType(props.policy.policyType) ? <AccountAsVaultTag cloudProvider={backupPolicyNativeProvider(props.policy)} region={props.policy.schedules?.[0]?.region || "Source region"} /> : !!matchingVaults?.length && matchingVaults.map(_temp2)}</div>;
    $[0] = allBackupPolicyProperties;
    $[1] = enabled;
    $[2] = isAuthorized;
    $[3] = props;
    $[4] = snackbar;
    $[5] = vaultsSectionExpanded;
    $[6] = T0;
    $[7] = T1;
    $[8] = T2;
    $[9] = T3;
    $[10] = T4;
    $[11] = t0;
    $[12] = t1;
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = togglePolicyEnabledState;
  } else {
    T0 = $[6];
    T1 = $[7];
    T2 = $[8];
    T3 = $[9];
    T4 = $[10];
    t0 = $[11];
    t1 = $[12];
    t10 = $[13];
    t11 = $[14];
    t12 = $[15];
    t2 = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
    togglePolicyEnabledState = $[24];
  }
  let t13;
  if ($[122] !== T0 || $[123] !== t0 || $[124] !== t1 || $[125] !== t2 || $[126] !== t3 || $[127] !== t4) {
    t13 = <T0 direction={t0} className={t1} sx={t2}>{t3}{t4}</T0>;
    $[122] = T0;
    $[123] = t0;
    $[124] = t1;
    $[125] = t2;
    $[126] = t3;
    $[127] = t4;
    $[128] = t13;
  } else {
    t13 = $[128];
  }
  let t14;
  if ($[129] !== T1 || $[130] !== t13) {
    t14 = <T1>{t13}</T1>;
    $[129] = T1;
    $[130] = t13;
    $[131] = t14;
  } else {
    t14 = $[131];
  }
  let t15;
  if ($[132] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      paddingRight: 0,
      paddingTop: "24px"
    };
    $[132] = t15;
  } else {
    t15 = $[132];
  }
  let t16;
  if ($[133] !== props.policy || $[134] !== props.vaults) {
    t16 = <AccordionDetails sx={t15}>{isContinuousPolicyType(props.policy.policyType) ? <BackupPolicyContinuousSchedulesTable policy={props.policy} /> : <BackupPolicySchedulesTable policy={props.policy} vaults={props.vaults} />}</AccordionDetails>;
    $[133] = props.policy;
    $[134] = props.vaults;
    $[135] = t16;
  } else {
    t16 = $[135];
  }
  let t17;
  if ($[136] !== T2 || $[137] !== t14 || $[138] !== t16 || $[139] !== t5 || $[140] !== t6) {
    t17 = <T2 expanded={t5} onChange={t6}>{t14}{t16}</T2>;
    $[136] = T2;
    $[137] = t14;
    $[138] = t16;
    $[139] = t5;
    $[140] = t6;
    $[141] = t17;
  } else {
    t17 = $[141];
  }
  let t18;
  if ($[142] !== T3 || $[143] !== t17 || $[144] !== t7 || $[145] !== t8 || $[146] !== t9) {
    t18 = <T3 sx={t7}>{t8}{t9}{t17}</T3>;
    $[142] = T3;
    $[143] = t17;
    $[144] = t7;
    $[145] = t8;
    $[146] = t9;
    $[147] = t18;
  } else {
    t18 = $[147];
  }
  let t19;
  if ($[148] !== props.policy.name) {
    t19 = <>The <span className="font-semibold">{props.policy.name}</span>{" "}backup policy will stop being applied to resources. Existing snapshots will be kept until their retention expires.</>;
    $[148] = props.policy.name;
    $[149] = t19;
  } else {
    t19 = $[149];
  }
  let t20;
  if ($[150] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setDisablePolicyDialogOpen(false);
    $[150] = t20;
  } else {
    t20 = $[150];
  }
  let t21;
  if ($[151] !== togglePolicyEnabledState) {
    t21 = () => {
      togglePolicyEnabledState();
      setDisablePolicyDialogOpen(false);
    };
    $[151] = togglePolicyEnabledState;
    $[152] = t21;
  } else {
    t21 = $[152];
  }
  let t22;
  if ($[153] !== disablePolicyDialogOpen || $[154] !== t19 || $[155] !== t21) {
    t22 = <ConfirmationDialog isOpen={disablePolicyDialogOpen} title="Disable backup policy" description={t19} onCancel={t20} onConfirm={t21} buttonTitle="Disable Policy" />;
    $[153] = disablePolicyDialogOpen;
    $[154] = t19;
    $[155] = t21;
    $[156] = t22;
  } else {
    t22 = $[156];
  }
  let t23;
  if ($[157] !== t10 || $[158] !== t18 || $[159] !== t22) {
    t23 = <>{t10}{t18}{t22}</>;
    $[157] = t10;
    $[158] = t18;
    $[159] = t22;
    $[160] = t23;
  } else {
    t23 = $[160];
  }
  let t24;
  if ($[161] !== T4 || $[162] !== t11 || $[163] !== t12 || $[164] !== t23) {
    t24 = <T4 disabled={t11} data-testid={t12}>{t23}</T4>;
    $[161] = T4;
    $[162] = t11;
    $[163] = t12;
    $[164] = t23;
    $[165] = t24;
  } else {
    t24 = $[165];
  }
  return t24;
}
function _temp2(vault_0) {
  return <VaultTag key={vault_0.id} vault={vault_0} />;
}
function _temp(text, ref) {
  return <Typography noWrap={true} variant="h3" ref={ref}>{text}</Typography>;
}